import React, { useEffect, useState } from 'react';
import { View, Text } from 'react-native';
import { useLazyQuery } from '@apollo/client';
import {
  pageCount
} from '@preflighttech/preflight-tables/dist/functions/pagination';
import { useDefaults } from '@preflighttech/preflight-tables/dist/Context';

import { useSearchParams } from 'app/src/utils/routing';

import Table from './index';

const getEntries = ({ data, queryName }) => {
  if (!data) { return []; }

  return data[queryName].edges.map(edge => edge.node);
};

const variablesWithOptions = variables => {
  let { scopes, search, sort, sortDirection, options, ...other } = variables;
  options = { ...options, scopes, search, sort, sortDirection };

  return { options, ...other };
};

const orderFromSearchParams = searchParams => {
  const order = [];

  searchParams.forEach((value, key) => {
    const sortMatch = key.match(/sort\[(\d)\]/);

    if (sortMatch && value) {
      let direction = 'asc';

      searchParams.forEach((value, key) => {
        const sortDirectionMatch = key.match(/sort-direction\[(\d)\]/);
        if (sortDirectionMatch && sortDirectionMatch[1] === sortMatch[1]) {
          direction = value;
        }
      });

      order.push({ key: value, direction });
    }
  });

  return order.length > 0 ? order : null;
};

const updateOrderSearchParams = ({ order, searchParams }) => {
  const keysToDelete = [];

  searchParams.forEach((value, key) => {
    if (key.match(/^sort/)) { keysToDelete.push(key); }
  });

  keysToDelete.forEach(key => searchParams.delete(key));

  order.forEach(({ key, direction }, index) => {
    searchParams.set(`sort[${index}]`, key);

    if ('desc' === direction) {
      searchParams.set(`sort-direction[${index}]`, direction);
    }
  });
};

export const RelayTable = props => {
  const {
    query,
    queryName,
    columns,
    lengthMenu,
    paginationLinks,
    queryVariables,
    dataUpdated,
    setRefetch,
    setSettings,
    styles,
    component,
    componentProps,
    buttons,
    htmlTable,
    disableSearch,
    alwaysSort,
    refetch,
    initialLoadComponent,
    isLoadingComponent,
    pageLength: initialPageLength,
    pollInterval,
    disableOrderFromSearchParams,
  } = useDefaults(props);

  const [searchParams, setSearchParams] = useSearchParams();

  const [variables, setVariables] = useState();
  const [entries, setEntries] = useState([]);
  const [count, setCount] = useState(0);
  const [order, setOrder] = useState(
    disableOrderFromSearchParams ? null : orderFromSearchParams(searchParams)
  );
  const [page, setPage] = useState(parseInt(searchParams.get('page') || 1) - 1);
  const [pageLength, setPageLength] = useState(
    searchParams.get('page-length') ?
      parseInt(searchParams.get('page-length')) :
      ('All' === initialPageLength ? 0 : (initialPageLength || 10))
  );
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search-term'));

  const [called, setCalled] = useState(false);
  const [initialLoadCompleted, setInitialLoadCompleted] = useState(false);

  const [getData, { loading, data, error }] = useLazyQuery(query, {
    pollInterval: (pollInterval || (30 * 60 * 1000)),
    fetchPolicy: 'network-only',
    onCompleted: newData => {
      if (dataUpdated) {
        dataUpdated(newData);
      }
    }
  });

  const updateEntries = options => {
    const { newPage, newPageLength, newSearchTerm, newVariables } = options;
    let { newOrder } = options;

    let limit = undefined === newPageLength ? pageLength : newPageLength;
    if (0 === limit) { limit = null; }

    const offset = window.btoa(
      String((undefined === newPage ? page : newPage) * limit)
    );

    const search = undefined === newSearchTerm ? searchTerm : newSearchTerm;

    const tableVariables = { limit: limit || 100000, offset, search };

    if (newOrder) {
      tableVariables.sort = newOrder[0] ? newOrder[0].key : undefined;
      tableVariables.sortDirection =
        newOrder[0] ? newOrder[0].direction : undefined;
    } else {
      tableVariables.sort = order[0] ? order[0].key : undefined;
      tableVariables.sortDirection =
        order[0] ? order[0].direction : undefined;
    }

    getData(
      {
        variables: variablesWithOptions({
          ...tableVariables,
          ...(newVariables || variables || {})
        })
      }
    );

    if (undefined !== newVariables) { setVariables(newVariables) };

    if (undefined !== newOrder) {
      updateOrderSearchParams({ order: newOrder, searchParams });
      setSearchParams(searchParams);
      setOrder(newOrder);
    }

    if (undefined !== newPage) {
      searchParams.set('page', newPage + 1);
      setSearchParams(searchParams);
      setPage(newPage);
    }

    if (undefined !== newPageLength) {
      searchParams.set('page-length', newPageLength);
      setSearchParams(searchParams);
      setPageLength(newPageLength);
    }

    if (undefined !== newSearchTerm) {
      searchParams.set('search-term', newSearchTerm);
      setSearchParams(searchParams);
      setSearchTerm(newSearchTerm);
    }

    if (setSettings) {
      setSettings({search, order: (newOrder || order)});
    }
  };

  if (!order) {
    const newOrder = [];
    columns.forEach(column => {
      if ('asc' === column.sort) {
        newOrder.push({ key: column.key, direction: 'asc' });
      } else if ('desc' === column.sort) {
        newOrder.push({ key: column.key, direction: 'desc' });
      }
    });

    setOrder(newOrder);
    if (setSettings) { setSettings({order: newOrder}) }
  }

  if (!called && !data && order) {
    setCalled(true);
    updateEntries({ newVariables: queryVariables });
  }

  useEffect(() => {
    if (setRefetch) {
      setRefetch(() => () => updateEntries({ newVariables: queryVariables }));
    }
  }, [setRefetch]);

  if (data && JSON.stringify(queryVariables) !== JSON.stringify(variables)) {
    updateEntries({ newVariables: queryVariables });
    setRefetch(() => () => updateEntries({ newVariables: queryVariables }));
  }

  if (data && data[queryName]) {
    // Save entries and count to state to avoid jumpiness when updateEntries
    // fetches new data (due to table rows disappearing).
    const dataEntries = getEntries({ data, queryName });

    if (JSON.stringify(dataEntries) !== JSON.stringify(entries)) {
      setEntries(dataEntries);
    }

    if (data[queryName].totalCount != count) {
      setCount(data[queryName].totalCount)

      const maxPage = pageCount(data[queryName].totalCount, pageLength) - 1;
      if (maxPage < page) {
        updateEntries({ newPage: maxPage });
      }
    }
  }

  if (!initialLoadCompleted) {
    if (data) { setInitialLoadCompleted(true); }

    return (
      <View>
        { initialLoadComponent || <Text>Loading...</Text> }
      </View>
    );
  }

  return (
    <>
      {error && (
        <View style={{borderWidth: 2, borderColor: 'red'}}>
          <Text style={{fontSize: 18}}>Error: {error.message}</Text>
        </View>
      )}
      <View style={{ overflowX: 'auto' }}>
        <Table
          entries={entries}
          columns={columns}
          order={order}
          updateEntries={updateEntries}
          page={page}
          count={count}
          lengthMenu={lengthMenu}
          pageLength={pageLength}
          paginationLinks={paginationLinks}
          searchTerm={searchTerm}
          styles={styles}
          component={component}
          componentProps={componentProps}
          buttons={buttons}
          htmlTable={htmlTable}
          disableSearch={disableSearch}
          alwaysSort={alwaysSort}
          isLoading={loading}
          isLoadingComponent={isLoadingComponent}
          refetch={
            false === refetch ? false :
            () => {
              setEntries([]); updateEntries({});
            }
          }
        />
      </View>
    </>
  );
};

export default RelayTable;
